import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, CheckCircle, XCircle, Clock, Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as authApi from '@/services/authApi';

const TransferRequests = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchRequests();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      // Get pending requests for receiving section
      const userSection = user?.sections?.[0]; // Assuming user has at least one section
      
      if (userSection && userSection !== 'FOB') {
        const pendingResponse = await apiClient.get('/internal-transfer/pending-requests', {
          params: { section: userSection }
        });
        setPendingRequests(pendingResponse.data.requests || []);
      }

      // Get accepted requests (for FOB users to ship)
      if (userSection === 'FOB' || user?.role === 'Admin') {
        const acceptedResponse = await apiClient.get('/internal-transfer/pending-requests');
        setAcceptedRequests(acceptedResponse.data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load transfer requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async (transferId) => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post('/internal-transfer/accept', {
        transfer_id: transferId
      });
      toast.success(response.data.message);
      fetchRequests(); // Refresh list
    } catch (error) {
      console.error('Error accepting transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept transfer');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectClick = (transfer) => {
    setSelectedTransfer(transfer);
    setShowRejectDialog(true);
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post('/internal-transfer/reject', {
        transfer_id: selectedTransfer.transfer_id,
        rejection_reason: rejectionReason.trim()
      });
      toast.success(response.data.message);
      setShowRejectDialog(false);
      fetchRequests(); // Refresh list
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject transfer');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmShipment = async (transferId) => {
    if (!window.confirm('Confirm shipment of this transfer?')) return;

    setIsProcessing(true);
    try {
      const response = await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: transferId
      });
      toast.success(response.data.message);
      toast.info(`Document Number: ${response.data.document_number}`, { duration: 5000 });
      fetchRequests(); // Refresh list
    } catch (error) {
      console.error('Error confirming shipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const userSection = user?.sections?.[0];
  const isFOBUser = userSection === 'FOB' || user?.role === 'Admin';
  const isInchargeOrSupervisor = ['Section Incharge', 'Section Supervisor', 'Admin'].includes(user?.role);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/dashboard')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Transfer Requests</h1>
              <p className="text-sm text-[#9CA3AF]">Manage internal transfer requests</p>
            </div>
          </div>
          <Button onClick={fetchRequests} variant="secondary" size="sm">
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Pending Requests (for receiving section) */}
        {!isFOBUser && isInchargeOrSupervisor && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Pending Requests
              </CardTitle>
              <CardDescription>
                Transfer requests awaiting your approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : pendingRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-16 h-16 mx-auto mb-3 opacity-20" />
                  <p>No pending transfer requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map((transfer) => (
                    <div
                      key={transfer.transfer_id}
                      className="border border-[#E5DFD0] rounded-lg p-4 hover:bg-[#FFFBF5] transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{transfer.transfer_id}</h3>
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                              Pending
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="text-gray-500">From:</span>{' '}
                              <strong>{transfer.from_section}</strong>
                            </div>
                            <div>
                              <span className="text-gray-500">Bundles:</span>{' '}
                              <strong>{transfer.bundle_count}</strong>
                            </div>
                            <div>
                              <span className="text-gray-500">Requested by:</span>{' '}
                              <strong>{transfer.requested_by_name}</strong>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>{' '}
                              <strong>{formatDate(transfer.requested_at)}</strong>
                            </div>
                          </div>
                          <details className="text-sm">
                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                              View Bundles ({transfer.bundle_count})
                            </summary>
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                              {transfer.bundle_codes?.map((code, idx) => (
                                <div key={code} className="font-mono text-xs py-1">
                                  {idx + 1}. {code}
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            onClick={() => handleAccept(transfer.transfer_id)}
                            disabled={isProcessing}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            onClick={() => handleRejectClick(transfer)}
                            disabled={isProcessing}
                            variant="outline"
                            className="border-red-500 text-red-600 hover:bg-red-50"
                            size="sm"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Accepted Requests (for FOB to ship) */}
        {isFOBUser && isInchargeOrSupervisor && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" />
                Ready to Ship
              </CardTitle>
              <CardDescription>
                Accepted transfers awaiting shipment confirmation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : acceptedRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Send className="w-16 h-16 mx-auto mb-3 opacity-20" />
                  <p>No transfers ready to ship</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {acceptedRequests.map((transfer) => (
                    <div
                      key={transfer.transfer_id}
                      className="border border-[#E5DFD0] rounded-lg p-4 hover:bg-[#FFFBF5] transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{transfer.transfer_id}</h3>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              Accepted
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="text-gray-500">To:</span>{' '}
                              <strong>{transfer.to_section}</strong>
                            </div>
                            <div>
                              <span className="text-gray-500">Bundles:</span>{' '}
                              <strong>{transfer.bundle_count}</strong>
                            </div>
                            <div>
                              <span className="text-gray-500">Accepted by:</span>{' '}
                              <strong>{transfer.accepted_by_name}</strong>
                            </div>
                            <div>
                              <span className="text-gray-500">Accepted at:</span>{' '}
                              <strong>{formatDate(transfer.accepted_at)}</strong>
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleConfirmShipment(transfer.transfer_id)}
                          disabled={isProcessing}
                          className="bg-green-600 hover:bg-green-700 ml-4"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Confirm Shipment
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card for non-authorized users */}
        {!isInchargeOrSupervisor && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800">
                    <strong>Access Restricted:</strong> Only Section Incharges and Supervisors can manage transfer requests.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Reject Transfer Request
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this transfer request.
              The requester will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedTransfer && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>Transfer ID:</strong> {selectedTransfer.transfer_id}</p>
                <p><strong>From:</strong> {selectedTransfer.from_section}</p>
                <p><strong>Bundles:</strong> {selectedTransfer.bundle_count}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="reason">Rejection Reason *</Label>
              <Textarea
                id="reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Wrong section selected, insufficient capacity, etc."
                className="mt-2"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              disabled={isProcessing || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {isProcessing ? 'Processing...' : 'Reject Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransferRequests;
